import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password);
      toast.success("Password set — let's practice");
      navigate("/scenarios");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar minimal />
      <div className="max-w-md mx-auto px-6 pt-16 pb-24">
        <div className="text-xs tracking-[0.18em] uppercase text-stone-500 mb-3">LeaderSim · by Emergence</div>
        <h1 className="font-display text-3xl md:text-4xl text-stone-900 tracking-tight">
          Set your password.
        </h1>
        <p className="mt-3 text-stone-600">
          Your account is created by your organization&apos;s admin. Enter your work email to set your password.
        </p>

        <form onSubmit={handleSubmit} className="mt-10 space-y-5" data-testid="signup-form">
          <div>
            <Label htmlFor="email" className="text-sm font-medium text-stone-700 mb-2 block">Email</Label>
            <Input
              id="email"
              data-testid="signup-email-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl h-12 bg-white border-stone-200 focus-visible:ring-[#b81f24]/20 focus-visible:border-[#b81f24]"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-sm font-medium text-stone-700 mb-2 block">New password</Label>
            <Input
              id="password"
              data-testid="signup-password-input"
              type="password"
              autoComplete="new-password"
              minLength={6}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-xl h-12 bg-white border-stone-200 focus-visible:ring-[#b81f24]/20 focus-visible:border-[#b81f24]"
            />
            <p className="text-xs text-stone-500 mt-1.5">At least 6 characters.</p>
          </div>
          <Button
            data-testid="signup-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-md bg-[#b81f24] hover:bg-[#9a1a1f] text-white text-base"
          >
            {loading ? "Setting password…" : "Set password"}
          </Button>
        </form>
        <p className="mt-6 text-sm text-stone-500">
          Already have an account?{" "}
          <Link to="/login" data-testid="signup-go-login" className="text-[#b81f24] hover:text-[#9a1a1f] font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
